import React, { useMemo } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { Area, AreaChart, ResponsiveContainer, Tooltip } from 'recharts';

import { TRACO } from '../../utils/series';

/**
 * A trajetória em miniatura, no cabeçalho do bloco.
 *
 * POR QUE AQUI E NÃO NUM BLOCO PRÓPRIO: "Trajetória" era um bloco inteiro com
 * peso zero na nota, proibido de classificar qualquer coisa e com um indicador
 * inviável (Efeito Tesoura exige dois balanços). Um cartão na home e uma coluna
 * na navegação para entregar duas setas. As duas variações que de fato existem
 * viram isto: uma linha de 40px ao lado do número que ela contextualiza.
 *
 * `connectNulls={false}` não é detalhe: ligar fevereiro direto a junho faz a
 * inclinação mentir sobre a velocidade da mudança, que é inventar número na
 * camada visual.
 *
 * A ÁREA SOB A LINHA NÃO É ENFEITE: é o que dá corpo a 44px de altura.
 *
 * Como linha pura, a trajetória saía um fio de 2px atravessando uma faixa vazia
 * — no escuro, quase invisível, e no cabeçalho de um bloco de 900px de largura
 * ela parecia sujeira. O preenchimento em degradê, da cor da própria série até
 * transparente, transforma o fio num volume que se enxerga de relance sem
 * competir com o número que ele contextualiza. O identificador é único por
 * série para dois gráficos na mesma dobra não compartilharem degradê.
 */
let contadorDeGradiente = 0;

const Sparkline = ({ serie = [], rotulos = [], cor, rotulo, formatar, altura = 44 }) => {
  const idDoGradiente = useMemo(() => {
    contadorDeGradiente += 1;
    return `sparkline-${contadorDeGradiente}`;
  }, []);
  const dados = useMemo(
    () => serie.map((valor, i) => ({ valor, rotulo: rotulos[i] || `Período ${i + 1}` })),
    [serie, rotulos]
  );

  const medidos = serie.filter((v) => v !== null && v !== undefined);
  if (medidos.length < 2) return null;

  const ultimo = medidos[medidos.length - 1];

  return (
    <Box sx={{ minWidth: 0 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
        <Typography variant="caption" color="text.secondary" noWrap>
          {rotulo}
        </Typography>
        <Typography variant="caption" className="valor-numerico" sx={{ fontWeight: 600 }}>
          {formatar ? formatar(ultimo) : ultimo ?? TRACO}
        </Typography>
      </Stack>
      <Box sx={{ height: altura, mt: 0.5 }} aria-hidden="true">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={dados} margin={{ top: 6, right: 2, bottom: 2, left: 2 }}>
            <defs>
              <linearGradient id={idDoGradiente} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={cor} stopOpacity={0.22} />
                <stop offset="100%" stopColor={cor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <Tooltip
              cursor={{ stroke: cor, strokeOpacity: 0.25 }}
              contentStyle={{ fontSize: 12, borderRadius: 8, padding: '4px 8px' }}
              labelFormatter={(_, carga) => carga?.[0]?.payload?.rotulo || ''}
              formatter={(valor) => [formatar ? formatar(valor) : valor, rotulo]}
            />
            <Area
              type="monotone"
              dataKey="valor"
              stroke={cor}
              strokeWidth={2}
              fill={`url(#${idDoGradiente})`}
              // A ÁREA NASCE NO MENOR VALOR DA SÉRIE, não no zero do eixo.
              //
              // Com a base no zero, uma série quase plana — margem de
              // contribuição em 33% nos três meses — pinta o retângulo inteiro:
              // medido, o cabeçalho de Resultado saiu com um bloco escuro
              // chapado no lugar da trajetória, sem linha visível. Ancorada no
              // mínimo, a área vira a fita entre o piso e a curva, que é o que
              // a variação de fato ocupa.
              baseValue="dataMin"
              dot={false}
              activeDot={{ r: 3 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default Sparkline;
